import { useEffect, useState } from 'react';
import { RotateCcw, Save } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DeliverySettings } from '@/api/settings';
import { DeliverySettingsEditor } from '@/components/features/delivery/DeliverySettingsEditor';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button, TenantSelector } from '@/components/ui';
import { SubsectionSwitch } from '@/components/ui/SubsectionSwitch';
import { fallbackCanvas } from '@/features/delivery/fallbackCanvas';
import {
  fallbackSectionIds,
  fallbackSectionRegistry,
  findFallbackSection,
  type FallbackSectionId,
} from '@/features/delivery/fallbackSections';
import { useSettings } from '@/hooks/useSettings';
import { useSubsectionParam } from '@/hooks/useSubsectionParam';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useTenantScope } from '@/hooks/useTenantScope';
import { useToast } from '@/hooks/useToast';

export function SettingsPage() {
  const { tenantId: tenantIdForApi, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();
  const { settings, isLoading, updateSettings, isUpdating } = useSettings(tenantIdForApi);
  const { messengerStatus } = useTenantConfig(tenantIdForApi);
  const { showSuccess, showError } = useToast();
  const [localSettings, setLocalSettings] = useState<DeliverySettings | null>(null);
  const [sectionId, setSectionId] = useSubsectionParam('section', fallbackSectionIds, 'clinic');
  const section = findFallbackSection(sectionId);

  useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  const ownCount = Object.keys(localSettings?.broadcast_group_delivery_settings || {}).length;
  const groupCount = localSettings?.broadcast_groups?.length || 0;
  const sectionItems = fallbackSectionRegistry.map((item) => ({
    id: item.id,
    label: item.label,
    meta: item.id === 'own' ? String(ownCount) : item.id === 'shared' ? String(Math.max(groupCount - ownCount, 0)) : undefined,
  }));

  if (requireTenantSelection || isLoading || !localSettings) {
    return (
      <MainLayout headerTitle="Умные фолбэки" disableMainScroll>
        <div className="space-y-3 p-4">
          {requireTenantSelection ? (
            <div className="max-w-xs">
              <TenantSelector value={selectedTenantId || ''} onChange={setSelectedTenant} testId="select-tenant-settings" />
            </div>
          ) : null}
          <p className={fallbackCanvas.hint}>{requireTenantSelection ? 'Без клиники цепочка каналов не показывается.' : 'Читаем настройки.'}</p>
        </div>
      </MainLayout>
    );
  }

  const handleSave = async () => {
    try {
      await updateSettings({
        channel_priority: localSettings.channel_priority,
        disabled_channels: localSettings.disabled_channels || [],
        fallback_rules: localSettings.fallback_rules,
        max_fallback_depth: localSettings.max_fallback_depth,
        cooldown_minutes: localSettings.cooldown_minutes,
        quiet_hours: localSettings.quiet_hours,
        broadcast_group_modes: localSettings.broadcast_group_modes || {},
        broadcast_group_delivery_settings: localSettings.broadcast_group_delivery_settings || {},
        broadcast_daily_limit: localSettings.broadcast_daily_limit,
        broadcast_group_daily_limits: localSettings.broadcast_group_daily_limits || {},
        disable_fallback_groups: localSettings.disable_fallback_groups || [],
      });
      showSuccess('Настройки сохранены');
    } catch (error: any) {
      showError(error.response?.data?.error || 'Ошибка при сохранении настроек');
    }
  };

  return (
    <MainLayout
      headerTitle="Умные фолбэки"
      disableMainScroll
      headerActions={
        <>
          <Button variant="secondary" onClick={() => settings && setLocalSettings(settings)} disabled={isUpdating}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Сбросить
          </Button>
          <Button onClick={handleSave} disabled={isUpdating} isLoading={isUpdating}>
            <Save className="mr-2 h-4 w-4" />
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 space-y-3 border-b border-shell-border bg-shell-surface px-4 py-3">
          <p className="text-sm leading-5 text-shell-muted">{section.lead}</p>
          {isSuperAdmin ? (
            <div className="max-w-xs">
              <TenantSelector
                value={selectedTenantId || ''}
                onChange={setSelectedTenant}
                showLabel={false}
                testId="select-tenant-settings"
              />
            </div>
          ) : null}
          <SubsectionSwitch
            items={sectionItems}
            value={sectionId}
            onChange={(id) => setSectionId(id as FallbackSectionId)}
            testId="fallback-section"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <DeliverySettingsEditor
          settings={localSettings}
          onChange={setLocalSettings}
          section={sectionId}
          onSectionChange={setSectionId}
        />
        {sectionId === 'clinic' ? (
          <div className="mt-4 rounded-xl border border-shell-border bg-shell-surface p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium text-shell-strong">Клиентские аккаунты</p>
                <p className={fallbackCanvas.hint}>
                  Telegram Client и MAX Client не входят в цепочку фолбэков рассылок. Они видны в сообщениях и автоответах.
                </p>
              </div>
              <Link to="/messenger-clients" className="text-sm font-medium text-shell-primary hover:text-shell-primary-strong">
                Настроить
              </Link>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-md border border-shell-border bg-shell-elevated px-2 py-1 text-xs text-shell-text">
                Telegram Client: {messengerStatus.telegram_client ? 'подключён' : 'не подключён'}
              </span>
              <span className="rounded-md border border-shell-border bg-shell-elevated px-2 py-1 text-xs text-shell-text">
                MAX Client: {messengerStatus.max_client ? 'подключён' : 'не подключён'}
              </span>
            </div>
          </div>
        ) : null}
        </div>
      </div>
    </MainLayout>
  );
}
